import { useQuery } from '@tanstack/react-query';
import { Forecast, ForecastSourceType } from '@domain/forecast/models/Forecast';
import { container } from '@infrastructure/container';
import { WeeklyForecast } from '@domain/forecast/models/WeeklyForecast';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ForecastRun } from '@domain/forecast/models/ForecastRun';
import { useRef, useEffect } from 'react';


interface UseForecastOptions {
  sourceType?: ForecastSourceType;
  source?: string;
  connector?: string;
}

export const useForecast = ({ sourceType, source, connector }: UseForecastOptions) => {

  const {
    data: forecast,
    isLoading: isLoading,
    error: forecastError
  } = useQuery<Forecast>({
    queryKey: ['forecast', sourceType, source, connector],
    queryFn: async () => {
      const result = await container.forecastRepository.getLatestForecast(sourceType || ForecastSourceType.PRODUCT, source, connector);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    enabled: !!sourceType && !!source && !!connector,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
  });


  return {
    forecast,
    isLoading: isLoading,
    error: forecastError,

  };
};

export const useAggregatedForecast = (options?: {
  /**
   * Poll interval while waiting for the first forecast (e.g. sales onboarding).
   * Stops automatically once forecast data is present.
   */
  pollUntilReadyMs?: number;
}) => {
  const {
    data: aggregatedForecast,
    isLoading: isLoadingAggregatedForecast,
    isFetched: isFetchedAggregatedForecast,
    error: aggregatedForecastError
  } = useQuery<Omit<WeeklyForecast, 'forecastId'>[]>({
    queryKey: ['aggregated-forecast'],
    queryFn: async () => {
      const result = await container.forecastRepository.getAggregatedForecast();
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: (data) => {
      if (!options?.pollUntilReadyMs) return false;
      if (data && data.length > 0) return false;
      return options.pollUntilReadyMs;
    },
  });


  return {
    aggregatedForecast,
    isLoading: isLoadingAggregatedForecast,
    isFetched: isFetchedAggregatedForecast,
    error: aggregatedForecastError
  };
};



export const useRunForecast = (productId: string) => {
  const queryClient = useQueryClient();

  const prevForecastRunsRef = useRef<ForecastRun[] | undefined>(undefined);

  // Use React Query to store shouldUpdate state, shared across all hook instances
  const { data: shouldUpdate = false } = useQuery<boolean>({
    queryKey: ['forecast-should-update', productId],
    queryFn: () => false, // Default value
    enabled: !!productId,
    staleTime: Infinity, // Never stale, we manage this manually
  });

  const setShouldUpdate = (value: boolean) => {
    queryClient.setQueryData(['forecast-should-update', productId], value);
  };

  const { data: forecastRuns, isLoading: isLoadingForecastRuns, error: forecastRunsError } = useQuery<ForecastRun[]>({
    queryKey: ['forecast-runs', productId],
    queryFn: async () => {
      const result = await container.forecastRepository.getProductRuns(productId);
      if (result.status === 'error') throw new Error(result.error.message);
      return result.data;
    },
    enabled: !!productId,
    refetchInterval: 5000, // poll every 5 seconds  
  });

  // Effect to check if a forecast run disappeared
  useEffect(() => {
    const prevForecastRuns = prevForecastRunsRef.current;
    if (
      prevForecastRuns &&
      forecastRuns &&
      prevForecastRuns.length > 0 &&
      forecastRuns.length < prevForecastRuns.length
    ) {
      // One or more forecastRuns disappeared, invalidate forecast cache
      queryClient.invalidateQueries({ queryKey: ['forecast', 'sales', productId, 'bol'] });
    }
    prevForecastRunsRef.current = forecastRuns;
  }, [forecastRuns, productId, queryClient]);

  const { mutateAsync: runProductForecast } = useMutation({
    mutationFn: async ({ productId }: { productId: string }) => {
      const result = await container.forecastRepository.runProductForecast(productId);
      if (result.status === "error") throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forecast-runs', variables.productId] });
      // Reset shouldUpdate after successful forecast run
      queryClient.setQueryData(['forecast-should-update', variables.productId], false);
    }
  });

  return {
    shouldUpdate,
    setShouldUpdate,
    forecastRuns,
    isLoading: isLoadingForecastRuns,
    error: forecastRunsError,
    runProductForecast,
  };
};
